import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { ExpandMore } from "@mui/icons-material";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import {
  FORMS_FORMS,
  FORMS_FORM_DUPLICATE,
  FORMS_STATUS_COUNTS,
} from "../../config/urls";
import FormPreview from "./Components/FormPreview";
import PublishDialog from "./Builder/PublishDialog";
import {
  FORM_CATEGORY_OPTIONS,
  formatCategoryName,
  getFormCategories,
} from "./Components/utils";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
} from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import AppChip from "../../common/app-chip";
import AppDropdown from "../../common/app-dropdown";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";
import { formatWithTzLabel } from "../../utils/dateUtils";


const ROWS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const WRITE_ROLES = ["admin", "manager"];
const PUBLISHING_REFRESH_MS = 5000;

const STATUS_CHIPS = [
  { value: "", label: "All", countKey: "total" },
  { value: "DRAFT", label: "Draft", countKey: "DRAFT" },
  { value: "PUBLISHED", label: "Published", countKey: "PUBLISHED" },
  { value: "DEPRECATED", label: "Deprecated", countKey: "DEPRECATED" },
];

const STATUS_DOT_COLORS = {
  PUBLISHED: "#1FA45A",
  PUBLISHING: "#1B76D1",
  DRAFT: "#B7791F",
  DEPRECATED: "#6A7284",
  BLOCKED: "#C53030",
};

const formatUsedIn = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value !== "number") return value;
  if (value === 0) return "—";
  return `${value} ${value === 1 ? "template" : "templates"}`;
};

const formatUpdated = (value) => {
  const date = dayjs(value);
  return date.format(date.isSame(dayjs(), "year") ? "D MMM" : "D MMM YYYY");
};

const statusLabel = (status) =>
  status ? status.charAt(0) + status.slice(1).toLowerCase() : "";

const ViewForms = (props) => {
  const tenant = localStorage.getItem("tenant");
  const canWrite = WRITE_ROLES.includes(localStorage.getItem("role"));
  const isAdmin = localStorage.getItem("role") === "admin";
  const { getValidToken } = useAuth();

  const [forms, setForms] = useState([]);
  const [count, setCount] = useState(0);
  // null until the first response, so the chips don't show 0 and the empty state
  // doesn't flash while the first request is in flight.
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ message: "", severity: "error" });
  const [page, setPage] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [previewForm, setPreviewForm] = useState(null);
  const [formToDelete, setFormToDelete] = useState(null);
  const [formToRetry, setFormToRetry] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [menu, setMenu] = useState({ anchorEl: null, row: null });

  const hasActiveFilters = Boolean(
    debouncedSearch || statusFilter || categoryFilter,
  );

  // Search runs 300 ms after typing stops.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchForms();
  }, [page, debouncedSearch, statusFilter, categoryFilter]);

  // While a form is publishing, keep refreshing until Meta has answered.
  useEffect(() => {
    if (!forms.some((form) => form.status === "PUBLISHING")) return undefined;
    const timer = setTimeout(() => {
      fetchForms(true);
      fetchCounts();
    }, PUBLISHING_REFRESH_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms]);

  // Chip counts follow the search and category filter, not the status chip.
  useEffect(() => {
    fetchCounts();
  }, [debouncedSearch, categoryFilter]);

  const getHeaders = async () => {
    const token = await getValidToken();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  };

  const buildFilterParams = (includeStatus) => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (categoryFilter) params.set("category", categoryFilter);
    if (includeStatus && statusFilter) params.set("status", statusFilter);
    return params;
  };

  // `silent` refreshes in the background without the table's loading spinner.
  const fetchForms = async (silent = false) => {
    if (!silent) setLoading(true);

    try {
      const params = buildFilterParams(true);
      params.set("limit", ROWS_PER_PAGE);
      params.set("offset", page * ROWS_PER_PAGE);

      const response = await fetch(`${FORMS_FORMS}?${params}`, {
        headers: await getHeaders(),
      });
      if (!response.ok) throw new Error("Failed to fetch forms");

      const data = await response.json();
      setForms(data.results);
      setCount(data.count);
    } catch (error) {
      console.error("Error fetching forms:", error);
      setAlert({ message: "Failed to fetch forms", severity: "error" });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchCounts = async () => {
    try {
      const response = await fetch(
        `${FORMS_STATUS_COUNTS}?${buildFilterParams(false)}`,
        { headers: await getHeaders() },
      );
      if (!response.ok) throw new Error("Failed to fetch counts");

      const data = await response.json();
      setCounts({ ...data.counts, total: data.total });
    } catch (error) {
      console.error("Error fetching form counts:", error);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    setStatusFilter("");
    setCategoryFilter("");
    setPage(0);
  };

  const fetchFormDetail = async (id) => {
    const res = await fetch(`${FORMS_FORMS}${id}/`, {
      headers: await getHeaders(),
    });
    if (!res.ok) throw new Error("Failed to fetch form");
    return res.json();
  };

  const handleView = async (id) => {
    try {
      setPreviewForm(await fetchFormDetail(id));
    } catch (error) {
      console.error(error);
      setAlert({ message: "Failed to load form", severity: "error" });
    }
  };

  const handleContinueEditing = async (id) => {
    try {
      const data = await fetchFormDetail(id);
      props.onNavigate("form_builder", {
        id: data?.id,
        name: data?.name,
        categories: getFormCategories(data),
        status: data?.status,
        source: "view_forms",
      });
    } catch (error) {
      console.error(error);
      setAlert({ message: "Failed to load form", severity: "error" });
    }
  };

  const handleOpenSubmissions = (row, tab = 0) => {
    props.onNavigate("form_submissions", {
      id: row.id,
      name: row.name,
      status: row.status,
      tab,
    });
  };

  const handleDuplicate = async (id) => {
    try {
      const response = await fetch(FORMS_FORM_DUPLICATE(id), {
        method: "POST",
        headers: await getHeaders(),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to duplicate form");
      }

      setAlert({
        message: `Created draft "${data.name}"`,
        severity: "success",
      });
      // Page 0 shows the new draft first (newest update first).
      if (page === 0) {
        fetchForms();
      } else {
        setPage(0);
      }
      fetchCounts();
    } catch (error) {
      setAlert({
        message: error?.message || "Failed to duplicate form",
        severity: "error",
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!formToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(`${FORMS_FORMS}${formToDelete.id}/`, {
        method: "DELETE",
        headers: await getHeaders(),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || "Failed to delete form");
      }

      setAlert({ message: "Form deleted", severity: "success" });
      // Step back a page when the last row of a later page was deleted.
      if (forms.length === 1 && page > 0) {
        setPage(page - 1);
      } else {
        fetchForms();
      }
      fetchCounts();
    } catch (error) {
      setAlert({
        message: error?.message || "Failed to delete form",
        severity: "error",
      });
    } finally {
      setDeleting(false);
      setFormToDelete(null);
    }
  };

  const handleRowClick = (row) => {
    if (row.status === "DRAFT" && canWrite) {
      handleContinueEditing(row.id);
    } else if (canWrite && row.status !== "PUBLISHING") {
      handleOpenSubmissions(row);
    } else {
      handleView(row.id);
    }
  };

  const actionLinkSx = {
    ...typography.bodySmall,
    color: ColorsConstants.primaryPurple100,
    cursor: "pointer",
    whiteSpace: "nowrap",
    background: "none",
    border: 0,
    p: 0,
    "&:hover": { textDecoration: "underline" },
  };

  const closeMenu = () => setMenu({ anchorEl: null, row: null });

  const columns = [
    {
      id: "name",
      label: "Form",
      render: (row) => (
        <Stack>
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {row.name}
          </Typography>
          <Typography
            sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray60 }}
          >
            {getFormCategories(row).map(formatCategoryName).join(", ") || "—"}
          </Typography>
        </Stack>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (row) => (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            ...typography.bodySmall,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: STATUS_DOT_COLORS[row.status] || "#6A7284",
            }}
          />
          {statusLabel(row.status)}
        </Box>
      ),
    },
    {
      id: "screens_count",
      label: "Screens",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {row.screens_count}
        </Typography>
      ),
    },
    {
      id: "submissions_30d",
      label: "Submissions (30 days)",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {row.submissions_30d ?? "—"}
        </Typography>
      ),
    },
    {
      id: "used_in",
      label: "Used in",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {formatUsedIn(row.used_in)}
        </Typography>
      ),
    },
    {
      id: "updated_at",
      label: "Updated",
      render: (row) => (
        <Tooltip
          title={`${formatWithTzLabel(row.updated_at)}${
            row.updated_by ? ` by ${row.updated_by}` : ""
          }`}
        >
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {formatUpdated(row.updated_at)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      render: (row) => {
        const isDraft = row.status === "DRAFT";
        const showMenu = canWrite;
        return (
          <Stack direction="row" spacing={1.5} alignItems="center">
            {isDraft && canWrite ? (
              <Box
                component="button"
                type="button"
                sx={actionLinkSx}
                onClick={(e) => {
                  e.stopPropagation();
                  handleContinueEditing(row.id);
                }}
              >
                Continue editing
              </Box>
            ) : (
              <>
                {canWrite && row.status !== "PUBLISHING" && (
                  <Box
                    component="button"
                    type="button"
                    sx={actionLinkSx}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenSubmissions(row);
                    }}
                  >
                    Submissions
                  </Box>
                )}
                {canWrite && row.status === "PUBLISHING" && row.publishing_stale && (
                  <Box
                    component="button"
                    type="button"
                    sx={actionLinkSx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormToRetry(row);
                    }}
                  >
                    Retry publish
                  </Box>
                )}
                {canWrite && row.status === "PUBLISHED" && (
                  <Box
                    component="button"
                    type="button"
                    sx={actionLinkSx}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenSubmissions(row, 1);
                    }}
                  >
                    Data
                  </Box>
                )}
                <Box
                  component="button"
                  type="button"
                  sx={actionLinkSx}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleView(row.id);
                  }}
                >
                  View
                </Box>
              </>
            )}
            {showMenu && (
              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  aria-label="More actions"
                  aria-haspopup="menu"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenu({ anchorEl: e.currentTarget, row });
                  }}
                  sx={{ color: "#6C7C93" }}
                >
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  const renderEmptyState = () => (
    <Stack
      spacing={2}
      sx={{
        alignItems: "center",
        py: 8,
      }}
    >
      <Typography sx={{ ...typography.h5 }}>
        {hasActiveFilters ? "No forms match" : "No forms yet"}
      </Typography>
      {hasActiveFilters ? (
        <ButtonTwo title="Clear filters" onClick={clearFilters} />
      ) : (
        canWrite && (
          <ButtonOne
            title="Create form"
            onClick={() => props.onNavigate("whatsapp_forms", { tab: 0 })}
          />
        )
      )}
    </Stack>
  );

  return (
    <Box sx={{ px: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography
            sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray60 }}
          >
            Message Templates › WhatsApp Forms
          </Typography>
          <Typography sx={{ ...typography.h5 }}>View Forms</Typography>
        </Box>
        {/* Disabled Unmatched replies for now */}
        {/* {isAdmin && (
          <Box
            component="button"
            type="button"
            sx={actionLinkSx}
            onClick={() => props.onNavigate("form_unmatched_replies", {})}
          >
            Unmatched replies
          </Box>
        )}*/}
      </Stack>

      {alert.message && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ message: "", severity: "error" })}
          sx={{ mb: 2, "& .MuiAlert-message": typography.bodyLarge }}
        >
          {alert.message}
        </Alert>
      )}

      <Box
        sx={{
          border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
          mb: 2,
        }}
      >
        {/* Search, status chips and category filter */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            px: 2.25,
            py: 1.5,
            borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          }}
        >
          <Box sx={{ width: 280, maxWidth: "100%" }}>
            <TableSearchInput
              searchQuery={searchQuery}
              onSearchChange={(e) =>
                setSearchQuery(e.target.value.slice(0, 50))
              }
              tooltip="Search by form name"
              placeholder="Search forms"
            />
          </Box>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {STATUS_CHIPS.map((chip) => {
              const selected = statusFilter === chip.value;
              return (
                <AppChip
                  key={chip.label}
                  label={
                    counts ? `${chip.label} ${counts[chip.countKey] ?? 0}` : chip.label
                  }
                  onClick={() => {
                    setStatusFilter(chip.value);
                    setPage(0);
                  }}
                  variant="outlined"
                  sx={{
                    height: 28,
                    px: 0.5,
                    borderColor: selected
                      ? ColorsConstants.primaryBackgroundColor
                      : borderColors.cardBorderPrimaryGrayColor,
                    color: selected
                      ? ColorsConstants.primaryBackgroundColor
                      : grayFontColor,
                    backgroundColor: selected
                      ? dashboardCardBgColor
                      : "transparent",
                    "&:hover": {
                      backgroundColor: dashboardCardBgColor,
                    },
                  }}
                />
              );
            })}
          </Stack>

          <Box sx={{ ml: "auto" }}>
            <AppDropdown
              menuItems={[
                { label: "All categories", value: "" },
                ...FORM_CATEGORY_OPTIONS,
              ]}
              selectedValue={categoryFilter}
              onMenuSelect={(value) => {
                setCategoryFilter(value);
                setPage(0);
              }}
              endIcon={<ExpandMore />}
              sx={{ minWidth: 180, justifyContent: "space-between" }}
            >
              {categoryFilter
                ? formatCategoryName(categoryFilter)
                : "All categories"}
            </AppDropdown>
          </Box>
        </Box>

        {!loading && count === 0 ? (
          renderEmptyState()
        ) : (
          <AppTable
            columns={columns}
            rows={forms}
            rowKey="id"
            loading={loading}
            maxHeight={500}
            onRowClick={handleRowClick}
            containerSx={{
              scrollbarWidth: "thin",
              scrollbarColor: "#C5CEDB #F3F6FB",
              "&::-webkit-scrollbar": { height: "8px", width: "8px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#F3F6FB",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#C5CEDB",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#A8B4C7",
              },
            }}
            headCellSx={{
              ...typography.bodySmall,
              backgroundColor: dashboardCardBgColor,
              color: grayFontColor,
              padding: "9px 18px",
              borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            }}
            bodyCellSx={{ padding: "10px 18px" }}
            rowSx={() => ({
              backgroundColor: "#FFFFFF",
              "&:hover": { backgroundColor: dashboardCardBgColor },
              "& td": {
                borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                borderTop: "none",
              },
              "&:last-child td": { borderBottom: "none" },
            })}
            paperSx={{
              border: "none",
              borderRadius: 0,
              boxShadow: "none",
              mb: 0,
            }}
            count={count}
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
          />
        )}
      </Box>

      <Menu
        anchorEl={menu.anchorEl}
        open={Boolean(menu.anchorEl)}
        onClose={closeMenu}
      >
        <MenuItem
          sx={typography.bodyLarge}
          onClick={() => {
            handleDuplicate(menu.row.id);
            closeMenu();
          }}
        >
          Duplicate
        </MenuItem>
        {menu.row?.status === "DRAFT" && (
          <MenuItem
            sx={typography.bodyLarge}
            onClick={() => {
              setFormToDelete(menu.row);
              closeMenu();
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Preview dialog */}
      <Dialog
        open={Boolean(previewForm)}
        onClose={() => setPreviewForm(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: { borderRadius: "12px", border: "1px solid #DCDFE5" },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          {previewForm && <FormPreview formJson={previewForm.form_json} />}
        </DialogContent>
      </Dialog>

      {/* Retry publish for a form whose publish was lost */}
      <PublishDialog
        open={Boolean(formToRetry)}
        formId={formToRetry?.id}
        onClose={() => {
          setFormToRetry(null);
          fetchForms(true);
          fetchCounts();
        }}
        onPublished={() => {
          setFormToRetry(null);
          setAlert({ message: "Form published", severity: "success" });
          fetchForms(true);
          fetchCounts();
        }}
      />

      {/* Delete confirmation */}
      <Dialog
        open={Boolean(formToDelete)}
        onClose={() => !deleting && setFormToDelete(null)}
      >
        <DialogTitle sx={typography.h5}>Delete form</DialogTitle>
        <DialogContent>
          <DialogContentText sx={typography.bodyLarge}>
            Delete "{formToDelete?.name}"? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <ButtonTwo
            title="Cancel"
            onClick={() => setFormToDelete(null)}
            disabled={deleting}
          />
          <ButtonOne
            title="Delete"
            onClick={handleConfirmDelete}
            disabled={deleting}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewForms;
